import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlignLeft,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronRight,
  Eye,
  GitBranch,
  GripVertical,
  Hash,
  Info,
  LockKeyhole,
  MoveRight,
  RefreshCw,
  Save,
  Search,
  SquareCheck,
  Trash2,
  Type,
} from 'lucide-react';
import { ApiError } from '@/lib/api-client';
import { useI18n } from '@/lib/i18n';
import {
  deleteTicketForm,
  getTicketForm,
  publishTicketForm,
  unpublishTicketForm,
  updateTicketForm,
  updateTicketFormFields,
  type TicketForm,
  type TicketFormFieldConfig,
  type TicketFormFieldUpdate,
} from '@/lib/ticket-form-api';
import { getTicketFields, type TicketFieldRegistryField } from '@/lib/ticket-field-registry-api';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, DropdownSelect, EmptyState, ErrorState, Input, SkeletonText, Textarea, useConfirm } from '@/components/ui';

type BuilderField = TicketFieldRegistryField & TicketFormFieldConfig;
type FieldSection = 'default' | 'custom' | 'nested';

const SECTION_KEYS: FieldSection[] = ['default', 'custom', 'nested'];
const OPTION_SOURCES = ['STATIC', 'AGENTS', 'USERS', 'GROUPS', 'COUNTRIES', 'CUSTOM'] as const;

function isRequired(field: BuilderField) {
  return field.requiredOverride === true
    || field.requiredForAgents
    || field.requiredForCustomers
    || field.requiredForClosure;
}

function sectionFor(field: BuilderField): FieldSection {
  if (field.sourceType === 'nested_field' || field.dependsOnFieldKey) return 'nested';
  if (field.sourceType.startsWith('custom_')) return 'custom';
  return 'default';
}

function fieldTypeLabel(rendererType: string) {
  return rendererType
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function FieldTypeIcon({ rendererType }: { rendererType: string }) {
  if (rendererType === 'multi_line_text') return <AlignLeft className="h-4 w-4" aria-hidden="true" />;
  if (rendererType === 'dropdown' || rendererType === 'multiselect') return <ChevronDown className="h-4 w-4" aria-hidden="true" />;
  if (rendererType === 'checkbox' || rendererType === 'radio') return <SquareCheck className="h-4 w-4" aria-hidden="true" />;
  if (rendererType === 'date') return <CalendarDays className="h-4 w-4" aria-hidden="true" />;
  if (rendererType === 'number' || rendererType === 'decimal') return <Hash className="h-4 w-4" aria-hidden="true" />;
  if (rendererType === 'lookup') return <Search className="h-4 w-4" aria-hidden="true" />;
  if (rendererType === 'dependent') return <GitBranch className="h-4 w-4" aria-hidden="true" />;
  return <Type className="h-4 w-4" aria-hidden="true" />;
}

function mergeBuilderFields(registry: TicketFieldRegistryField[], form: TicketForm): BuilderField[] {
  const configs = new Map(form.fields.map((field) => [field.fieldKey, field]));
  const registryPositions = new Map(registry.map((field) => [field.fieldKey, field.position]));
  return registry
    .map((field, index) => {
      const config = configs.get(field.fieldKey);
      return {
        ...field,
        position: config?.position ?? index + 1,
        enabled: config?.enabled ?? false,
        requiredOverride: config?.requiredOverride ?? null,
        visibleToCustomers: config?.visibleToCustomers ?? field.displayedToCustomers,
      };
    })
    .sort((left, right) => {
      const leftConfigured = configs.has(left.fieldKey);
      const rightConfigured = configs.has(right.fieldKey);
      if (leftConfigured !== rightConfigured) return leftConfigured ? -1 : 1;
      if (leftConfigured && rightConfigured) return left.position - right.position;
      return (registryPositions.get(left.fieldKey) ?? 0) - (registryPositions.get(right.fieldKey) ?? 0);
    });
}

// ponytail: send all fields with trimmed properties (omit defaults the
// backend recomputes). The backend partial-merge handles either full or
// partial payloads, but we always send the full list to avoid deployment
// sync issues. Per-field default omission alone brings the payload from
// ~9.5KB to ~3.2KB — well under the ALB WAF 8KB limit.
function fieldsPayload(fields: BuilderField[]): TicketFormFieldUpdate[] {
  return fields.map((field, index): TicketFormFieldUpdate => {
    const config: TicketFormFieldUpdate = {
      fieldKey: field.fieldKey,
      position: index + 1,
      enabled: field.enabled,
    };
    if (field.requiredOverride !== null) config.requiredOverride = field.requiredOverride;
    if (field.visibleToCustomers !== true) config.visibleToCustomers = field.visibleToCustomers;
    if (field.optionSourceType === 'CUSTOM') {
      config.optionSourceType = field.optionSourceType;
      if (field.optionSourceId) config.optionSourceId = field.optionSourceId;
    }
    return config;
  });
}

export function TicketFormBuilderPage() {
  const { confirm } = useConfirm();
  const { t, tOr } = useI18n();
  const errorMessage = React.useCallback(
    (error: unknown) => (error instanceof ApiError ? String(error.message) : t('set.requestFailed')),
    [t],
  );
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [form, setForm] = React.useState<TicketForm | null>(null);
  const [fields, setFields] = React.useState<BuilderField[]>([]);
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [collapsedSections, setCollapsedSections] = React.useState<Record<FieldSection, boolean>>({ default: false, custom: false, nested: false });
  const [draggingFieldKey, setDraggingFieldKey] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [publishing, setPublishing] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  const loadForm = React.useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [nextForm, registry] = await Promise.all([getTicketForm(id), getTicketFields()]);
      setForm(nextForm);
      setName(nextForm.name);
      setDescription(nextForm.description ?? '');
      setFields(mergeBuilderFields(registry, nextForm));
      setDirty(false);
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [id, errorMessage]);

  React.useEffect(() => {
    void loadForm();
  }, [loadForm]);

  React.useEffect(() => {
    if (!dirty) return undefined;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [dirty]);

  async function leaveBuilder(event: React.MouseEvent<HTMLAnchorElement>) {
    if (!dirty) return;
    event.preventDefault();
    const destination = event.currentTarget.getAttribute('href');
    if (destination && await confirm({ title: t('set.tb.discardTitle'), description: t('set.tb.discardDesc'), confirmLabel: t('set.tb.leave'), variant: 'default' })) navigate(destination);
  }

  function updateFields(nextFields: BuilderField[]) {
    setFields(nextFields.map((field, index) => ({ ...field, position: index + 1 })));
    setDirty(true);
    setNotice(null);
  }

  function toggleField(field: BuilderField) {
    updateFields(fields.map((current) => current.fieldKey === field.fieldKey
      ? { ...current, enabled: !current.enabled }
      : current));
  }

  function dropField(targetKey: string) {
    if (!draggingFieldKey || draggingFieldKey === targetKey) return;
    const sourceIndex = fields.findIndex((field) => field.fieldKey === draggingFieldKey);
    const targetIndex = fields.findIndex((field) => field.fieldKey === targetKey);
    if (sourceIndex < 0 || targetIndex < 0) return;
    const nextFields = [...fields];
    const [movedField] = nextFields.splice(sourceIndex, 1);
    nextFields.splice(targetIndex, 0, movedField);
    updateFields(nextFields);
    setDraggingFieldKey(null);
  }

  async function save() {
    if (!form || !id) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError(t('set.tb.nameRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const [updatedMetadata, updatedFields] = await Promise.all([
        updateTicketForm(id, { name: trimmedName, description: description.trim() }),
        updateTicketFormFields(id, fieldsPayload(fields)),
      ]);
      const nextForm = { ...updatedMetadata, fields: updatedFields.fields, fieldDefinitions: updatedFields.fieldDefinitions };
      setForm(nextForm);
      setName(nextForm.name);
      setDescription(nextForm.description ?? '');
      setFields((current) => mergeBuilderFields(current, nextForm));
      setDirty(false);
      setNotice(t('set.tb.saved'));
    } catch (saveError) {
      setError(errorMessage(saveError));
    } finally {
      setSaving(false);
    }
  }

  async function togglePublished() {
    if (!form || !id) return;
    setPublishing(true);
    setError(null);
    try {
      const updated = form.isPublished ? await unpublishTicketForm(id) : await publishTicketForm(id);
      setForm(updated);
      setNotice(updated.isPublished ? t('set.tb.nowPublished') : t('set.tb.nowUnpublished'));
    } catch (publishError) {
      setError(errorMessage(publishError));
    } finally {
      setPublishing(false);
    }
  }

  async function removeForm() {
    if (!form || form.isDefault || !id || !await confirm({ title: t('set.deleteFormTitle', { name: form.name }), description: t('set.deleteFormDescription'), confirmLabel: t('set.deleteForm') })) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteTicketForm(id);
      navigate('/settings/ticket-forms');
    } catch (deleteError) {
      setError(errorMessage(deleteError));
      setDeleting(false);
    }
  }

  if (!id) return <ErrorState message={t('set.formIdRequired')} />;
  if (loading) {
    return (
      <div className="space-y-6">
        <Card><CardContent className="p-6"><SkeletonText lines={2} /></CardContent></Card>
        <Card><CardContent className="p-6"><SkeletonText lines={8} /></CardContent></Card>
      </div>
    );
  }
  if (!form) return <ErrorState message={error ?? t('set.formNotFound')} onRetry={() => void loadForm()} />;

  const sections = SECTION_KEYS.map((section) => ({
    key: section,
    fields: fields.filter((field) => sectionFor(field) === section),
  }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-sm text-ink-subtle">
            <Link to="/settings" className="text-brand-700 hover:underline" onClick={leaveBuilder}>{t('set.tb.admin')}</Link>
            <span className="mx-2 text-ink-subtle">/</span>
            <Link to="/settings/ticket-forms" className="text-brand-700 hover:underline" onClick={leaveBuilder}>{t('set.ticketForms')}</Link>
            <span className="mx-2 text-ink-subtle">/</span>
            <span>{t('set.tb.builder')}</span>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Input
              aria-label={t('set.tb.titleAria')}
              value={name}
              onChange={(event) => { setName(event.target.value); setDirty(true); }}
              className="h-11 max-w-xl text-xl font-bold"
            />
            {form.isDefault && (
              <Badge variant="outline" className="inline-flex items-center gap-1">
                <LockKeyhole className="h-3 w-3" aria-hidden="true" />
                {t('set.tb.defaultLocked')}
              </Badge>
            )}
            <Badge variant={form.isPublished ? 'success' : 'outline'}>{form.isPublished ? t('set.published') : t('set.draft')}</Badge>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => navigate(`/admin/ticket-forms/${id}/preview`)}>
            <Eye className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('set.tb.previewPortal')}
          </Button>
          <Button onClick={() => void save()} disabled={saving || !dirty}>
            <Save className="mr-2 h-4 w-4" aria-hidden="true" />
            {saving ? t('set.saving') : t('set.saveChanges')}
          </Button>
        </div>
      </div>

      {error && <ErrorState message={error} onRetry={() => void loadForm()} />}
      {notice && (
        <Card className="border-brand-200 bg-brand-50">
          <CardContent className="flex items-center gap-3 p-4 text-sm text-brand-800">
            <Info className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{notice}</span>
          </CardContent>
        </Card>
      )}

      <Card className="border-brand-200 bg-brand-50">
        <CardContent className="flex gap-3 p-4">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-brand-700" aria-hidden="true" />
          <div className="text-sm text-brand-900">
            <p className="font-medium">{t('set.tb.configureTitle')}</p>
            <p className="mt-1 text-brand-800">{t('set.tb.configureDesc')}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base">{t('set.tb.formDetails')}</CardTitle>
              <p className="mt-1 text-xs text-ink-subtle">{t('set.tb.formDetailsHelp')}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => void loadForm()} disabled={loading || saving || dirty}>
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" /> {t('set.refresh')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Textarea
            id="form-description"
            label={t('set.description')}
            value={description}
            onChange={(event) => { setDescription(event.target.value); setDirty(true); }}
            rows={3}
            placeholder={t('set.tb.descPlaceholder')}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('set.tb.importedFields')}</CardTitle>
          <p className="mt-1 text-xs text-ink-subtle">{t('set.tb.importedHelp')}</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {sections.filter(({ fields: sectionFields }) => sectionFields.length > 0).map(({ key, fields: sectionFields }) => {
            const section = {
              title: t(`set.tb.section.${key}.title` as Parameters<typeof t>[0]),
              description: t(`set.tb.section.${key}.desc` as Parameters<typeof t>[0]),
            };
            const collapsed = collapsedSections[key];
            return (
              <section key={key} aria-labelledby={`${key}-fields-heading`}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between rounded-md border bg-surface-hover px-4 py-3 text-left hover:bg-surface-subtle"
                  onClick={() => setCollapsedSections((current) => ({ ...current, [key]: !current[key] }))}
                  aria-expanded={!collapsed}
                >
                  <span>
                    <span id={`${key}-fields-heading`} className="block text-sm font-semibold text-ink">{section.title} <span className="font-normal text-ink-subtle">({sectionFields.length})</span></span>
                    <span className="mt-1 block text-xs text-ink-subtle">{section.description}</span>
                  </span>
                  {collapsed ? <ChevronRight className="h-4 w-4 text-ink-subtle" aria-hidden="true" /> : <ChevronDown className="h-4 w-4 text-ink-subtle" aria-hidden="true" />}
                </button>
                {!collapsed && (
                  <div className="mt-2 space-y-2">
                    {sectionFields.length === 0 ? (
                      <p className="px-4 py-3 text-sm text-ink-subtle">{t('set.tb.noneInSection')}</p>
                    ) : sectionFields.map((field) => {
                      const required = isRequired(field);
                      return (
                        <div
                          key={field.fieldKey}
                          draggable
                          onDragStart={() => setDraggingFieldKey(field.fieldKey)}
                          onDragEnd={() => setDraggingFieldKey(null)}
                          onDragOver={(event) => { event.preventDefault(); event.stopPropagation(); }}
                          onDrop={(event) => { event.preventDefault(); event.stopPropagation(); dropField(field.fieldKey); }}
                          className={`flex items-start gap-3 rounded-md border p-3 transition ${field.enabled ? 'bg-surface' : 'bg-surface-hover opacity-70'} ${draggingFieldKey === field.fieldKey ? 'border-brand-400 shadow-sm' : ''}`}
                        >
                          <GripVertical className="mt-1 h-4 w-4 shrink-0 cursor-grab text-ink-subtle" aria-label={t('set.tb.dragReorder')} />
                          <div className="mt-0.5 rounded-md bg-surface-subtle p-2 text-ink-muted"><FieldTypeIcon rendererType={field.rendererType} /></div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-medium text-ink">{field.label}</p>
                              {field.isDefault && <Badge variant="outline">{t('set.default')}</Badge>}
                              {required && <Badge variant="default">{t('set.tb.required')}</Badge>}
                              {field.dependsOnFieldKey && <Badge variant="outline" className="inline-flex items-center gap-1"><MoveRight className="h-3 w-3" aria-hidden="true" /> {t('set.tb.nested')}</Badge>}
                            </div>
                            <p className="mt-1 text-xs text-ink-subtle">{tOr(`set.ftype.${field.rendererType}`, fieldTypeLabel(field.rendererType))} · <span className="font-mono">{field.fieldKey}</span></p>
                            {field.description && <p className="mt-1 text-sm text-ink-muted">{field.description}</p>}
                            {field.dependsOnFieldKey && <p className="mt-1 text-xs text-brand-700">{t('set.dependsOn', { field: field.dependsOnFieldKey })}</p>}
                            {field.rendererType === 'lookup' && field.enabled && (
                              <div className="mt-2">
                                <label className="text-xs font-medium text-ink-muted">
                                  {t('set.relationshipSource')}
                                  <DropdownSelect
                                    options={OPTION_SOURCES.map((source) => ({ value: source, label: t(`set.src.${source}`) }))}
                                    value={field.optionSourceType || 'STATIC'}
                                    onChange={(value) => {
                                      updateFields(fields.map((f) => f.fieldKey === field.fieldKey
                                        ? { ...f, optionSourceType: value as any }
                                        : f));
                                    }}
                                    searchable={false}
                                    className="ml-2 inline-block w-auto"
                                  />
                                </label>
                              </div>
                            )}
                          </div>
                          <label className="flex shrink-0 items-center gap-2 text-sm text-ink-muted">
                            <span className="sr-only">{t('set.tb.enableField', { label: field.label })}</span>
                            <input
                              type="checkbox"
                              checked={field.enabled}
                              disabled={false}
                              onChange={() => toggleField(field)}
                              className="h-4 w-4 rounded border-line-strong text-brand-700 focus:ring-brand-500"
                            />
                            <span>{field.enabled ? t('set.tb.enabled') : t('set.tb.disabled')}</span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
          {!fields.length && (
            <EmptyState
              title={t('set.tb.emptyTitle')}
              description={t('set.tb.emptyDesc')}
            />
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
        <Button variant="destructive" onClick={() => void removeForm()} disabled={form.isDefault || deleting}>
          <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
          {deleting ? t('set.tb.deleting') : t('set.deleteForm')}
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => void togglePublished()} disabled={publishing}>
            {form.isPublished ? t('set.unpublish') : t('set.publish')}
          </Button>
          <Button onClick={() => void save()} disabled={saving || !dirty}>
            <Check className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('set.saveChanges')}
          </Button>
        </div>
      </div>
    </div>
  );
}
